import { Resource } from '@/lib/types/resource';
import { User } from "@/lib/types/user";
import { useUsersStore } from "@/store/user";
import { useVisitorsStore } from "@/store/visitor";
import { cancelReservation, confirmReservation, createReservation, deleteReservation, fetchResourceOccupiedCapacity, getReservations, updateReservation, validateReservation } from "../api/reservation";
import { CreateReservationDTO, Reservation, ReservationDTO, ReservationStatus, ReservationType } from "../types/reservation";
import { ResourceType } from "../types/resourceType";
import { Visitor, VisitorType } from "../types/visitor";
import { visitorToDTO } from "./visitor";

// `from`/`to` are `yyyy-MM-dd` calendar-date strings, which is the wire contract of GET
// /rest/v1/reservations. They are deliberately NOT `Date`s: this function used to take instants and
// project them with the browser-local formatDateToyyyMMdd, which silently undid any resource-local
// day window a caller had computed (the slot timeline's, D-12) and shifted the requested span by a
// day whenever the two zones straddled midnight. Each caller now decides which clock the calendar
// date is read on — the calendar grid stays browser-local (SC5), the slot timeline is
// resource-local — and no instant crosses a browser-local formatter on the way down.
export async function fetchReservations(from: string, to: string, reservationType?: 'INTERNAL' | 'EXTERNAL', resourceTypes?: ResourceType[], resources?: Resource[], visitorTypes?: VisitorType[], own?: boolean): Promise<PagedResult<Reservation>> {
    const res = await getReservations({
        from,
        to,
        reservationType: reservationType,
        resourceTypes: resourceTypes,
        resources: resources,
        visitorTypes: visitorTypes,
        own
    })

    const usersStore = useUsersStore()
    const userIds = res.items.flatMap(dto => [dto.createdBy].filter((id): id is string => !!id))
    await usersStore.fetchByIds(userIds)

    const visitorsStore = useVisitorsStore()
    const visitorIds = res.items.flatMap(dto => dto.visitors)
    await visitorsStore.fetchByIds(visitorIds)

    const reservations = res.items.map(dto => dtoToReservation(dto, visitorsStore.visitors, usersStore.users));

    return {
        ...res,
        items: reservations
    }
}

export async function manageCreateReservation(data: Reservation): Promise<ErrorMessage | Reservation> {
    const res = await createReservation(reservationToCreateDTO(data))
    if (res && 'errorCode' in res) {
        return res
    }

    const usersStore = useUsersStore()
    const userIds = [res.createdBy].filter((id): id is string => !!id)
    await usersStore.fetchByIds(userIds)

    const users = usersStore.users
    const visitorsStore = useVisitorsStore()
    const visitorIds = res.visitors

    await visitorsStore.fetchByIds(visitorIds)

    const reservation = dtoToReservation(res, visitorsStore.visitors, users);

    return reservation;
}

export async function manageValidateReservation(data: Reservation): Promise<ErrorMessage | void> {
    const id = data.id !== '' ? data.id : undefined
    return await validateReservation(reservationToCreateDTO(data), id)
}

export async function manageUpdateReservation(data: Reservation): Promise<ErrorMessage | Reservation> {
    const res = await updateReservation(data.id, reservationToCreateDTO(data))

    if (res && 'errorCode' in res) {
        return res
    }

    const usersStore = useUsersStore()
    const userIds = [res.createdBy].filter((id): id is string => !!id)
    await usersStore.fetchByIds(userIds)

    const visitorsStore = useVisitorsStore()
    const visitorIds = res.visitors
    await visitorsStore.fetchByIds(visitorIds)

    const reservation = dtoToReservation(res, visitorsStore.visitors, usersStore.users);

    return reservation;
}

export async function manageConfirmReservation(id: string): Promise<ErrorMessage | Reservation> {
    const res = await confirmReservation(id)

    if (res && 'errorCode' in res) {
        return res
    }

    const usersStore = useUsersStore()
    const userIds = [res.createdBy].filter((id): id is string => !!id)
    await usersStore.fetchByIds(userIds)

    const visitorsStore = useVisitorsStore()
    const visitorIds = res.visitors
    await visitorsStore.fetchByIds(visitorIds)

    const reservation = dtoToReservation(res, visitorsStore.visitors, usersStore.users);

    return reservation;
}

export async function manageDeleteReservation(id: string): Promise<ErrorMessage | void> {
    return await deleteReservation(id)
}

export async function manageCancelReservation(id: string, reason?: string): Promise<ErrorMessage | void> {
    return await cancelReservation(id, reason);
}

export async function manageGetOccupiedCapacity(resource: Resource, from: Date, to: Date, excludeReservationId: string | null): Promise<{ total: number, available: number }> {
    const resourceId = resource.id
    const fromMs = from.getTime()
    const toMs = to.getTime()

    return fetchResourceOccupiedCapacity(resourceId, fromMs, toMs, excludeReservationId)
}

function dtoToReservation(dto: ReservationDTO, visitors: Visitor[], users: User[]): Reservation {
    const filteredVisitors = dto.visitors.map((visitorId: string) => visitors.find(v => v.id === visitorId)!);

    return {
        id: dto.id,
        title: dto.title,
        description: dto.description,
        attendees: dto.attendees,
        visitors: filteredVisitors,
        slots: dto.slots,
        status: ReservationStatus[dto.status as keyof typeof ReservationStatus],
        // The API serializes enums in PascalCase ("External"), while ReservationType values are uppercase ("EXTERNAL")
        type: ReservationType[dto.type as keyof typeof ReservationType],
        createdBy: users.find(user => user.id === dto.createdBy)!,
    }
}

/**
 * Formats a Unix-ms timestamp as a resource-local YYYY-MM-DD string. The en-CA locale is load-bearing, not cosmetic: it
 * yields zero-padded YYYY-MM-DD, which is lexicographically comparable, which is what lets the caller below compare two
 * days with a plain `<`.
 *
 * WR-02 (Phase 01 review finding) is CLOSED by TZ-01: as of delta `Files/0.0.5-0.0.6.sql`, `resources.timezone_id` holds
 * IANA identifiers rather than Windows ones, so Intl accepts every id the /timezones endpoint serves and the try branch
 * is now the normal path. The earlier note here said the opposite; it described the pre-migration world and would now
 * mislead every reader.
 *
 * The catch is deliberately RETAINED, as the D-09 degraded path rather than as the common case. A resource whose stored
 * id resolves to no served entry arrives here as `undefined` (see isPastUnconfirmed below) and falls back to the
 * operator's BROWSER-local day. Browser-local, not UTC: this is a single-site deployment, so the operator's clock tracks
 * the resource's, whereas a UTC fallback would lag the Rome day after every midnight and make the calendar badge and the
 * morning email disagree nightly.
 *
 * The backend's counterpart is now `Shared/TimeZoneResolver.Resolve`, which emits a once-per-resource `Log.Warning` and
 * falls back to UTC for the same class of id. So the two ends still disagree for a broken resource — but the backend's
 * half of that disagreement is searchable in `logs/log-{date}.txt` rather than silent.
 */
function localDateOf(ms: number, tzId: string | undefined): string {
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: tzId }).format(new Date(ms))
    } catch {
        return new Intl.DateTimeFormat('en-CA').format(new Date(ms))
    }
}

/**
 * Stateless eligibility predicate (D-13): a reservation is "past unconfirmed" when it is an External visit,
 * still Planned, and EVERY slot's resource-local end DATE is strictly before the resource-local today.
 * "Past" is day-based in the resource's timezone, not end-time-based. Shared by the calendar badge and the
 * ReservationDetail banner so the two indicators can never disagree.
 *
 * D-09: `?.timezone?.id` already tolerates the nullable `Resource.timezone`. A resource whose stored id resolves to no
 * served entry yields `undefined` here, which localDateOf reads as "use the browser's day" — so a degraded resource is
 * still EVALUATED by this predicate rather than silently dropped out of it. Both sides of the comparison take the same
 * `tzId`, so the fallback shifts both days together and the strict `<` stays meaningful.
 */
export function isPastUnconfirmed(reservation: Reservation, getResource: (id: string) => Resource | undefined): boolean {
    if (reservation.type !== ReservationType.External || reservation.status !== ReservationStatus.Planned) {
        return false
    }
    if (!reservation.slots || reservation.slots.length === 0) {
        return false
    }
    const nowMs = Date.now()
    return reservation.slots.every(slot => {
        const tzId = getResource(slot.resourceId)?.timezone?.id
        return localDateOf(slot.endMs, tzId) < localDateOf(nowMs, tzId)
    })
}

function reservationToCreateDTO(reservation: Reservation): CreateReservationDTO {
    return {
        title: reservation.title,
        description: reservation.description,
        attendees: reservation.attendees,
        visitors: reservation.visitors.map((visitor: Visitor) => visitorToDTO(visitor)),
        slots: reservation.slots,
        type: reservation.type
    }
}
